import {
  Button,
  Divider,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Step,
  StepIndicator,
  Stepper,
  Typography,
} from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  getGetClinicalReportByPatientQueryOptions,
  useCreateClinicalReportByPatient,
} from '../../../../../api/generated/diagnosis-controller/diagnosis-controller.ts';
import {
  AddressContactDto,
  AddressDto,
  ClinicalReportAnamnesisDtoPreviousDiagnoseStatus,
  ClinicalReportAnamnesisDtoPreviousTherapyStatus,
  ClinicalReportDtoOrgansItem,
  ClinicalReportMaterialDto,
  ClinicalReportMaterialDtoClinicalTestMaterial,
  ClinicalReportMaterialResultDto,
  ClinicalReportMaterialResultDtoClinicalDetectionMethod,
  ClinicalReportMaterialResultDtoTestResult,
  MedicationDto,
  PatientAddressDtoReferenceType,
  TherapyDto,
  TherapyDtoTherapyResult,
  TherapyMedicationDtoPhase,
} from '../../../../../api/generated/model';
import {
  getGetAnamnesesByPatientQueryOptions,
  getGetTherapyByPatientQueryOptions,
  useGetTherapyByPatientSuspense,
} from '../../../../../api/generated/patient-controller/patient-controller.ts';
import { getCurrentDate } from '../../../../../utils/date.ts';
import { blobToBase64 } from '../../../../../utils/file.ts';
import { getStringOrUndefined } from '../../../../../utils/string.ts';
import { ClinicalReportAnamnesisTherapyForm } from './ClinicalReportAnamnesisTherapyForm.tsx';
import { ClinicalReportDiagnosisForm } from './ClinicalReportDiagnosisForm.tsx';
import { ClinicalReportDoctorsForm } from './ClinicalReportDoctorsForm.tsx';

interface MaterialFormValues {
  clinicalTestMaterial: ClinicalReportMaterialDtoClinicalTestMaterial;
  otherMaterial: string | null;
  materialResults: MaterialResultFormValues[];
}

interface MaterialResultFormValues {
  clinicalDetectionMethod: ClinicalReportMaterialResultDtoClinicalDetectionMethod;
  testResult: ClinicalReportMaterialResultDtoTestResult | null;
}

interface FormValues {
  affectedOrgans: ClinicalReportDtoOrgansItem[];
  otherOrgan: string;
  laboratoryAddress: AddressDto | null;
  laboratoryContact: AddressContactDto | null;
  file: File | null;
  materials: MaterialFormValues[];
  previousDiagnose: ClinicalReportAnamnesisDtoPreviousDiagnoseStatus;
  previousDiagnoseDate: string;
  previousDiagnoseDateUnknown: boolean;
  previousTherpy: ClinicalReportAnamnesisDtoPreviousTherapyStatus;
  previousTherpyDate: string;
  previousTherpyDateUnknown: boolean;
  startPhase1: string;
  therapyEnd: string;
  therapyMedication: MedicationDto[];
  death: boolean;
  doctorAddress: AddressDto | null;
  doctorContact: AddressContactDto | null;
  treatingDoctorAddress: AddressDto | null;
  treatingDoctorContact: AddressContactDto | null;
  cantonDoctorAddress: AddressDto | null;
  cantonDoctorContact: AddressContactDto | null;
  notes: string;
}

const steps = ['Diagnose & Manifestation', 'Anamnese & Therapie', 'Ärzte & Bemerkungen'];

export function CreateClinicalReportDialog({
  open,
  onClose,
  patientId,
}: {
  open: boolean;
  onClose: () => void;
  patientId: string;
}) {
  const queryClient = useQueryClient();

  const { data: therapy } = useGetTherapyByPatientSuspense(patientId);

  const [activeStep, setActiveStep] = useState(1);
  const formMethods = useForm<FormValues>({
    defaultValues: getDefaultValues(),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const handleOnClose = (reason?: string) => {
    if (reason === 'backdropClick') {
      return;
    }
    setActiveStep(1);
    formMethods.reset();
    onClose();
  };

  const createClinicalReportMutation = useCreateClinicalReportByPatient({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries(getGetClinicalReportByPatientQueryOptions(patientId));
        void queryClient.invalidateQueries(getGetTherapyByPatientQueryOptions(patientId));
        void queryClient.invalidateQueries(getGetAnamnesesByPatientQueryOptions(patientId));
        handleOnClose();
      },
      onError: () => {
        toast.error('Beim Speichern des MKB ist ein Fehler aufgetreten');
      },
    },
  });

  useEffect(() => {
    if (open && therapy) {
      formMethods.reset(getDefaultValues(therapy), { keepDirtyValues: true });
    }
  }, [open, therapy]);

  return (
    <Modal open={open} onClose={(_, reason) => handleOnClose(reason)}>
      <ModalDialog sx={{ width: 850, overflowY: 'scroll' }}>
        <ModalClose />
        <Typography level={'h4'}>Neues MKB</Typography>
        <Stepper>
          {steps.map((step, index) => (
            <Step
              key={step}
              indicator={
                <StepIndicator
                  variant={activeStep <= index ? 'soft' : 'solid'}
                  color={activeStep < index ? 'neutral' : 'primary'}>
                  {index + 1}
                </StepIndicator>
              }>
              {step}
            </Step>
          ))}
        </Stepper>
        <Divider />
        <FormProvider {...formMethods}>
          <form
            onSubmit={formMethods.handleSubmit(async (data) =>
              createClinicalReportMutation.mutate({
                patientId: patientId,
                data: {
                  clinicalReport: {
                    patientId: patientId,
                    laboratoryAddress: data.laboratoryAddress
                      ? {
                          address: data.laboratoryAddress,
                          contact: data.laboratoryContact ?? undefined,
                          from: getCurrentDate(),
                          patientId: patientId,
                          referenceType: PatientAddressDtoReferenceType.CLINICAL_REPORT_LABORATORY,
                        }
                      : undefined,
                    doctorAddress: data.doctorAddress
                      ? {
                          address: data.doctorAddress,
                          contact: data.doctorContact ?? undefined,
                          from: getCurrentDate(),
                          patientId: patientId,
                          referenceType: PatientAddressDtoReferenceType.CLINICAL_REPORT_DOCTOR,
                        }
                      : undefined,
                    treatingDoctorAddress: data.treatingDoctorAddress
                      ? {
                          address: data.treatingDoctorAddress,
                          contact: data.treatingDoctorContact ?? undefined,
                          from: getCurrentDate(),
                          patientId: patientId,
                          referenceType: PatientAddressDtoReferenceType.CLINICAL_REPORT_TREATING_DOCTOR,
                        }
                      : undefined,
                    cantonDoctorAddress: data.cantonDoctorAddress
                      ? {
                          address: data.cantonDoctorAddress,
                          contact: data.cantonDoctorContact ?? undefined,
                          from: getCurrentDate(),
                          patientId: patientId,
                          referenceType: PatientAddressDtoReferenceType.CLINICAL_REPORT_CANTON_DOCTOR,
                        }
                      : undefined,
                    file: data.file
                      ? {
                          fileName: data.file.name,
                          file: await blobToBase64(data.file),
                        }
                      : undefined,
                    materials: data.materials.map((material) => ({
                      clinicalTestMaterial: material.clinicalTestMaterial,
                      otherMaterial: getStringOrUndefined(material.otherMaterial),
                      clinicalReportMaterialResults: material.materialResults.filter(
                        ({ testResult }) => testResult,
                      ) as ClinicalReportMaterialResultDto[],
                    })) as ClinicalReportMaterialDto[],
                    organs: data.affectedOrgans,
                    otherOrgan: getStringOrUndefined(data.otherOrgan),
                    notes: getStringOrUndefined(data.notes),
                  },
                  therapy: {
                    patientId: patientId,
                    startPhase1: data.startPhase1,
                    phase1Doctor: data.cantonDoctorAddress
                      ? {
                          address: data.cantonDoctorAddress,
                          contact: data.cantonDoctorContact ?? undefined,
                          from: getCurrentDate(),
                          patientId: patientId,
                          referenceType: PatientAddressDtoReferenceType.THERAPY_PHASE_1,
                        }
                      : undefined,
                    end: data.therapyEnd,
                    therapyMedication: data.therapyMedication.map((medication) => {
                      return {
                        from: data.startPhase1,
                        medication: medication,
                        phase: TherapyMedicationDtoPhase.PHASE_1,
                      };
                    }),
                    finished: data.death,
                    therapyResult: data.death ? TherapyDtoTherapyResult.DEATH : TherapyDtoTherapyResult.PENDING,
                  },
                  anamnesis: {
                    previousDiagnoseStatus: data.previousDiagnose,
                    previousDiagnoseDate: data.previousDiagnoseDateUnknown ? undefined : data.previousDiagnoseDate,
                    previousTherapyStatus: data.previousTherpy,
                    previousTherapyDate: data.previousTherpyDateUnknown ? undefined : data.previousTherpyDate,
                  },
                },
              }),
            )}>
            {activeStep === 1 && (
              <ClinicalReportDiagnosisForm
                affectedOrgans={'affectedOrgans'}
                otherOrgan={'otherOrgan'}
                file={'file'}
                materials={'materials'}>
                <Divider inset={'context'} />
                <Stack direction={'row'} justifyContent={'flex-end'} gap={1}>
                  <Button
                    color={'neutral'}
                    onClick={() => {
                      handleOnClose();
                    }}>
                    Abbrechen
                  </Button>
                  <Button
                    onClick={async () => {
                      const isValid = await formMethods.trigger();
                      if (isValid) {
                        setActiveStep(activeStep + 1);
                      }
                    }}>
                    Weiter
                  </Button>
                </Stack>
              </ClinicalReportDiagnosisForm>
            )}

            {activeStep === 2 && (
              <ClinicalReportAnamnesisTherapyForm
                previousDiagnose={'previousDiagnose'}
                previousDiagnoseDate={'previousDiagnoseDate'}
                previousDiagnoseDateUnknown={'previousDiagnoseDateUnknown'}
                previousTherpy={'previousTherpy'}
                previousTherpyDate={'previousTherpyDate'}
                previousTherpyDateUnknown={'previousTherpyDateUnknown'}
                startPhase1={'startPhase1'}
                therapyEnd={'therapyEnd'}
                therapyMedication={'therapyMedication'}
                death={'death'}
                therapy={therapy}>
                <Divider inset={'context'} />
                <Stack direction={'row'} justifyContent={'space-between'}>
                  <Button color={'neutral'} onClick={() => setActiveStep(activeStep - 1)}>
                    Zurück
                  </Button>
                  <Button
                    onClick={async () => {
                      const isValid = await formMethods.trigger();
                      if (isValid) {
                        setActiveStep(activeStep + 1);
                      }
                    }}>
                    Weiter
                  </Button>
                </Stack>
              </ClinicalReportAnamnesisTherapyForm>
            )}
            {activeStep === 3 && (
              <ClinicalReportDoctorsForm
                doctorAddress={'doctorAddress'}
                doctorContact={'doctorContact'}
                treatingDoctorAddress={'treatingDoctorAddress'}
                treatingDoctorContact={'treatingDoctorContact'}
                notes={'notes'}>
                <Divider inset={'context'} />
                <Stack direction={'row'} justifyContent={'space-between'}>
                  <Button color={'neutral'} onClick={() => setActiveStep(activeStep - 1)}>
                    Zurück
                  </Button>
                  <Button
                    disabled={!formMethods.formState.isDirty}
                    type={'submit'}
                    loading={createClinicalReportMutation.isPending}>
                    Speichern
                  </Button>
                </Stack>
              </ClinicalReportDoctorsForm>
            )}
          </form>
        </FormProvider>
      </ModalDialog>
    </Modal>
  );
}

function getDefaultValues(therapy?: TherapyDto) {
  return {
    affectedOrgans: [],
    otherOrgan: '',
    laboratoryAddress: null,
    laboratoryContact: null,
    file: null,
    materials: [
      {
        clinicalTestMaterial: ClinicalReportMaterialDtoClinicalTestMaterial.SPUTUM,
        otherMaterial: null,
        materialResults: materialResultDefaultValues,
      },
      {
        clinicalTestMaterial: ClinicalReportMaterialDtoClinicalTestMaterial.BRONCHIAL_SECRETION,
        otherMaterial: null,
        materialResults: materialResultDefaultValues,
      },
      {
        clinicalTestMaterial: ClinicalReportMaterialDtoClinicalTestMaterial.OTHER,
        otherMaterial: '',
        materialResults: materialResultDefaultValues,
      },
    ],
    previousDiagnose: ClinicalReportAnamnesisDtoPreviousDiagnoseStatus.NO,
    previousDiagnoseDate: '',
    previousDiagnoseDateUnknown: false,
    previousTherpy: ClinicalReportAnamnesisDtoPreviousTherapyStatus.NO,
    previousTherpyDate: '',
    previousTherpyDateUnknown: false,
    startPhase1: therapy?.startPhase1 ?? '',
    therapyEnd: therapy?.end ?? '',
    therapyMedication:
      therapy?.therapyMedication
        .filter((therapyMed) => therapyMed.phase === TherapyMedicationDtoPhase.PHASE_1 && !therapyMed.until)
        .map((therapyMed) => therapyMed.medication) ?? [],
    death: false,
    doctorAddress: null,
    doctorContact: null,
    treatingDoctorAddress: null,
    treatingDoctorContact: null,
    cantonDoctorAddress: therapy?.phase1Doctor?.address ?? null,
    cantonDoctorContact: therapy?.phase1Doctor?.contact ?? null,
    notes: '',
  };
}

const materialResultDefaultValues: MaterialResultFormValues[] = [
  {
    clinicalDetectionMethod: ClinicalReportMaterialResultDtoClinicalDetectionMethod.PCR,
    testResult: null,
  },
  {
    clinicalDetectionMethod: ClinicalReportMaterialResultDtoClinicalDetectionMethod.MICROSCOPY,
    testResult: null,
  },
  { clinicalDetectionMethod: ClinicalReportMaterialResultDtoClinicalDetectionMethod.CULTURE, testResult: null },
];
